import {Event} from 'main.core';

export class BaseDetailSlider extends Event.EventEmitter {
    constructor(sliderBlock, params) {
        super();
        this.sliderBlock = sliderBlock;
        this.imagesSets = null;

        this.setEventNamespace('BX.Asteq.Slider');

        this.mainSwiper = null;
        this.mainSwiperBlock = null;
        this.mainSwiperSlideTemplate = null;

        this.thumbsSwiper = null;
        this.thumbsSwiperBlock = null;
        this.thumbsSwiperSlideTemplate = null;

        this.gallery360Enabled = false;
        this.gallery360SlideTemplate = null;
        this.gallery360ThumbMarkup = '';
        this.gallery360ThumbPoster = '';
        this.gallery360PrependHtml = '';
        this.omitImageDimensions = false;

        this.initBlocks();
        this.initSwipers();
        this.initEvents();
    }

    getId() {
        if (!this.sliderBlock) {
            return null;
        }
        return this.sliderBlock.id;
    }

    initBlocks() {
        if (!this.sliderBlock) {
            return;
        }

        const mainSwiperBlock =
            this.sliderBlock.hasAttribute('data-main-swiper')
                ? this.sliderBlock
                : this.sliderBlock.querySelector('[data-main-swiper]');

        if (mainSwiperBlock) {
            this.mainSwiperBlock = mainSwiperBlock;
        }

        const thumbsSwiperBlock = this.sliderBlock.querySelector('[data-thumbs-swiper]');
        if (thumbsSwiperBlock) {
            this.thumbsSwiperBlock = thumbsSwiperBlock;
        }
    }

    initSwipers() {
        console.error('Необходимо переопределить метод инифиализации слайдеров');
    }

    initEvents() {
        const id = this.getId();
        if (!id) {
            return;
        }

        Event.EventEmitter.subscribe('BX.Asteq.Slider-' + id + ':setImages', this.onSetImages.bind(this));
        Event.EventEmitter.subscribe('BX.Asteq.Slider-' + id + ':setImagesSets', this.onSetImagesSets.bind(this));
    }

    isGallery360MainSlide(slide) {
        if (!slide) {
            return false;
        }

        return slide.hasAttribute('data-gallery-360-slide')
            || !!slide.querySelector('[data-gallery-360-slide]');
    }

    applyGallery360State(gallery360) {
        const has = !!(gallery360 && gallery360.HAS && gallery360.SOURCE_ID);
        this.gallery360Enabled = has;

        if (!has) {
            this.gallery360PrependHtml = '';
            this.gallery360ThumbPoster = '';
            this.gallery360ThumbMarkup = '';
            return;
        }

        const poster = gallery360.POSTER || '';
        const sourceId = parseInt(gallery360.SOURCE_ID, 10) || 0;

        this.gallery360ThumbPoster = poster;
        this.gallery360ThumbMarkup = this.buildGallery360ThumbHtml(poster);

        const engine = window.ProductGallery360;
        let slideTemplate = this.gallery360SlideTemplate;

        if (!slideTemplate && engine && typeof engine.getSlideTemplateForSlider === 'function') {
            const master = engine.getSlideTemplateForSlider(this);
            if (master) {
                slideTemplate = master.cloneNode(true);
                this.gallery360SlideTemplate = slideTemplate;
            }
        }

        if (engine && typeof engine.buildSlidePrependHtml === 'function' && slideTemplate) {
            this.gallery360PrependHtml = engine.buildSlidePrependHtml(
                slideTemplate,
                {
                    sourceId: sourceId,
                    poster: poster,
                }
            );
        } else {
            this.gallery360PrependHtml = '';
        }
    }

    buildGallery360ThumbHtml(posterSrc) {
        const poster = String(posterSrc || '')
            .replace(/&/g, '&amp;')
            .replace(/"/g, '&quot;');

        const imgHtml = poster
            ? `<img class="gallery-360-thumb__img" src="${poster}" alt="360°" width="100" height="100"/>`
            : '';

        return `<div class="swiper-slide gallery-360-thumb"><span class="gallery-360-thumb__inner">${imgHtml}<span class="gallery-360-thumb__badge">360°</span></span></div>`;
    }

    isGallery360ThumbSlide(slide) {
        if (!slide) {
            return false;
        }

        return slide.classList.contains('gallery-360-thumb')
            || !!slide.querySelector('.gallery-360-thumb');
    }

    setSwiperImages(images, swiper, slideTemplate, prependHtml, initialSlideIndex) {
        let newImagesStr = prependHtml || '';

        images.forEach((image) => {
            if (image.IS_360) {
                const poster = image.SRC || image.POSTER || this.gallery360ThumbPoster || '';
                if (poster) {
                    newImagesStr += this.buildGallery360ThumbHtml(poster);
                } else if (this.gallery360ThumbMarkup) {
                    newImagesStr += this.gallery360ThumbMarkup;
                } else {
                    newImagesStr += this.buildGallery360ThumbHtml('');
                }
                return;
            }

            const slide = slideTemplate.cloneNode(true);
            const imgEl = slide.tagName === 'IMG' ? slide : slide.querySelector('img');
            if (!imgEl) {
                return;
            }

            imgEl.src = image.SRC;
            if (!this.omitImageDimensions && image.WIDTH && image.HEIGHT) {
                imgEl.width = image.WIDTH;
                imgEl.height = image.HEIGHT;
            } else {
                imgEl.removeAttribute('width');
                imgEl.removeAttribute('height');
            }
            newImagesStr += slide.outerHTML;
        });

        const wrapper = swiper.wrapperEl;
        wrapper.innerHTML = newImagesStr;
        swiper.updateSlides();

        if (typeof initialSlideIndex === 'number') {
            swiper.slideTo(initialSlideIndex, 0);
        } else {
            swiper.setTranslate(0);
            swiper.slideTo(0);
        }
    }

    getGallery360PrependHtml() {
        if (!this.gallery360Enabled) {
            return '';
        }

        if (this.gallery360PrependHtml) {
            return this.gallery360PrependHtml;
        }

        if (!this.gallery360SlideTemplate) {
            return '';
        }

        return this.gallery360SlideTemplate.outerHTML;
    }

    setSlidesImages(images, initialSlideIndex) {
        if (!images) {
            return;
        }

        if (!this.mainSwiper) {
            console.error('Не найден основной свайпер');
            return;
        }

        if (!this.mainSwiperSlideTemplate) {
            console.error('Не найден шаблон слайда основного свайпера');
            return;
        }

        this.setSwiperImages(
            images,
            this.mainSwiper,
            this.mainSwiperSlideTemplate,
            this.getGallery360PrependHtml(),
            initialSlideIndex
        );

        if (this.mainSwiper?.thumbs) {
            this.mainSwiper.thumbs.update();
        }
    }

    setThumbImages(images, initialSlideIndex) {
        if (!images) {
            return;
        }

        if (!this.thumbsSwiper) {
            return;
        }

        if (!this.thumbsSwiperSlideTemplate) {
            console.error('Не найден шаблон слайда пагинации');
            return;
        }

        const thumbImages = images ? [...images] : [];
        if (this.gallery360Enabled) {
            const has360Thumb = thumbImages.some((image) => image.IS_360);
            if (!has360Thumb) {
                thumbImages.unshift({
                    IS_360: true,
                    SRC: this.gallery360ThumbPoster || '',
                });
            }
        }

        this.setSwiperImages(
            thumbImages,
            this.thumbsSwiper,
            this.thumbsSwiperSlideTemplate,
            '',
            initialSlideIndex
        );
    }

    setImages(imagesSets, initialSlideIndex) {
        if (imagesSets && imagesSets.gallery360 === undefined && imagesSets.GALLERY_360 !== undefined) {
            imagesSets.gallery360 = imagesSets.GALLERY_360;
        }

        this.setThumbImages(imagesSets.thumbs, initialSlideIndex);
        this.setSlidesImages(imagesSets.slides, initialSlideIndex);
        this.imagesSets = imagesSets;

        this.emit('afterSetImages');
    }

    onSetImages(event) {
        const data = event.getData();
        if (!data || !data.images) {
            return;
        }

        if (data.gallery360 !== undefined) {
            this.applyGallery360State(data.gallery360);
        }

        const images = data.images || {};
        if (images.gallery360 !== undefined) {
            this.applyGallery360State(images.gallery360);
        }

        const imagesSets = {
            thumbs: images.thumbs,
            slides: images.slides,
            details: images.details,
            gallery360: data.gallery360 !== undefined ? data.gallery360 : images.gallery360,
        };

        this.setImages(imagesSets, data.initialSlideIndex);
    }

    onSetImagesSets(e) {
        const imagesSets = e.getData().images;
        if (!imagesSets) {
            return;
        }

        this.imagesSets = imagesSets;
    }
}